import { useState, useEffect, useCallback, useRef } from "react";
import { useApolloClient } from "@apollo/client/react";
import { useMutation } from "@apollo/client/react";
import { gql } from "graphql-tag";
import { toast } from "sonner";
import type { AssistantMessage, AssistantToolCall } from "../types/assistant";
import { isProgressMessage } from "../types/assistant";
import type { ChatRating } from "../components/v2/ChatMessage";

// ── Shared GraphQL operations ──────────────────────────────────────────

const ASSISTANT_CONVERSATION_QUERY = gql`
  query AssistantConversationMessages($conversationId: UUID!) {
    assistantConversation(conversationId: $conversationId) {
      id
      messages {
        edges {
          node {
            id
            createdAt
            text
            isFromUser
            agent {
              id
              givenName
              familyName
            }
            toolCalls {
              id
              toolName
              status
              resultSummary
              executionDurationMs
              resultCount
            }
            feedback
            feedbackComment
            choiceOptions
            pendingFactContents
            notice
            references {
              id
              index
              type
              title
              excerpt
              externalLink
              sourceId
              sourceDocumentId
              sourceDocumentPageNumber
              sourceDatabaseTableId
              sourceDatabaseRowPkValues
              pastCase {
                caseNumber
                date
                text
              }
              sourceDatabaseColumnName
              sourceConversationId
              sourceConversationExternalId
            }
            identifiers {
              id
              attributeKey
              value
              label
              externalLink
            }
            attachments {
              id
              name
              contentType
              size
              isProcessed
              hasExtractedText
              useInAnswer
              textOnly
            }
          }
        }
      }
    }
  }
`;

const ASSISTANT_CONVERSATION_SUBSCRIPTION = gql`
  subscription AssistantConversationLive($conversationId: UUID!) {
    assistantConversation(conversationId: $conversationId) {
      id
      createdAt
      text
      isFromUser
      agent {
        id
        givenName
        familyName
      }
      toolCalls {
        id
        toolName
        status
        resultSummary
        executionDurationMs
        resultCount
      }
      choiceOptions
      pendingFactContents
      notice
      references {
        id
        index
        type
        title
        excerpt
        externalLink
        sourceId
        sourceDocumentId
        sourceDocumentPageNumber
        sourceDatabaseTableId
        sourceDatabaseRowPkValues
        pastCase {
          caseNumber
          date
          text
        }
        sourceDatabaseColumnName
        sourceConversationId
        sourceConversationExternalId
      }
      identifiers {
        id
        attributeKey
        value
        label
        externalLink
      }
      attachments {
        id
        name
        contentType
        size
        isProcessed
        hasExtractedText
        useInAnswer
        textOnly
      }
    }
  }
`;

const ASSISTANT_SEND_MESSAGE_MUTATION = gql`
  mutation AssistantSendMessage(
    $conversationId: UUID!
    $text: String!
    $panelContext: String
    $attachmentIds: [UUID!]
  ) {
    assistantConversationSendMessage(
      conversationId: $conversationId
      text: $text
      panelContext: $panelContext
      attachmentIds: $attachmentIds
    ) {
      id
    }
  }
`;

// ── Pure helpers ────────────────────────────────────────────────────────

/**
 * Derive whether the assistant has an open pending question waiting on the
 * user. Mirrors the "last persisted assistant" dedup used by ChoiceButtons
 * renderers: only the tail assistant turn's choiceOptions are considered
 * active. Once the user answers (tail becomes a user message) or the
 * assistant follows up with another turn, the question is no longer open.
 *
 * Progress messages (no createdAt) are already filtered out by the caller
 * when they pass `displayMessages`.
 *
 * Exported so it can be unit-tested without spinning up Apollo.
 */
export function deriveHasOpenPendingQuestion(
  displayMessages: AssistantMessage[],
  lastMessage: AssistantMessage | undefined,
): boolean {
  let lastPersistedAssistant: AssistantMessage | null = null;
  for (let i = displayMessages.length - 1; i >= 0; i--) {
    const m = displayMessages[i];
    if (!m.isFromUser) {
      lastPersistedAssistant = m;
      break;
    }
  }
  return (
    !!lastPersistedAssistant &&
    lastPersistedAssistant.id === lastMessage?.id &&
    !!lastPersistedAssistant.choiceOptions &&
    lastPersistedAssistant.choiceOptions.length > 0
  );
}

// ── Hook ────────────────────────────────────────────────────────────────

interface UseAssistantMessagesOptions {
  /** Active conversation ID. When null the hook is idle. */
  conversationId: string | null;
  /** Called when a persisted AI message arrives (e.g. to trigger answer-rewrite callbacks). */
  onAIMessage?: () => void;
  /** "source_review" | "draft_edit" — forwarded to the backend to select the right agent instructions. */
  panelContext?: string;
}

interface UseAssistantMessagesReturn {
  /** All messages including the current progress message. */
  messages: AssistantMessage[];
  /** Persisted messages only (no progress). */
  displayMessages: AssistantMessage[];
  /** Current progress message (live tool-call status), if any. */
  progressMessage: AssistantMessage | undefined;
  /** Composite waiting flag: sending, waiting for AI, or progress in flight. */
  isWaitingForResponse: boolean;
  /**
   * True from the moment a conversationId first needs fetching until that
   * fetch resolves. Already folded into isWaitingForResponse; exposed
   * separately for callers that want to distinguish "loading history" from
   * "waiting on the AI".
   */
  isInitialFetching: boolean;
  /** Whether a send mutation is currently in flight. */
  isSending: boolean;
  /** Whether the 5-minute timeout has been reached. */
  responseTimedOut: boolean;
  /** Whether the live subscription has encountered an error. */
  subscriptionError: boolean;
  /**
   * True when the most recent persisted assistant message carries
   * choiceOptions (Ja/Nein ERP prompt, ask_user_question, etc.) and
   * has not yet been superseded by a user reply or follow-up assistant
   * turn. Mirrors the "last persisted assistant" dedup logic used when
   * rendering ChoiceButtons — the question is considered open while
   * that same message is still the tail.
   */
  hasOpenPendingQuestion: boolean;
  /**
   * Send a follow-up message (adds optimistic user message + calls mutation).
   * Requires conversationId. `attachmentIds` are staged chat uploads to bind to
   * the turn.
   */
  sendMessage: (text: string, attachmentIds?: string[]) => void;
  /** Add a temporary user message to local state (for use before conversation creation). */
  addOptimisticUserMessage: (text: string) => void;
  /** Set the sending flag externally (for conversation-creation flows). */
  setSending: (sending: boolean) => void;
  /** Reset all state (messages, waiting flags, timeout). */
  reset: () => void;
  /** Update the rating and comment on a single message in local state. */
  updateMessageFeedback: (messageId: string, feedback: ChatRating, feedbackComment: string) => void;
}

export function useAssistantMessages({
  conversationId,
  onAIMessage,
  panelContext,
}: UseAssistantMessagesOptions): UseAssistantMessagesReturn {
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [waitingForAIResponse, setWaitingForAIResponse] = useState(false);
  const [responseTimeoutTriggered, setResponseTimeoutTriggered] = useState<
    string | null
  >(null);
  const [subscriptionError, setSubscriptionError] = useState(false);
  // True from the moment a conversationId first needs fetching until that
  // fetch resolves. Without this, adopting a late-arriving conversationId
  // (server pipeline still finishing) left messages/progressMessage empty and
  // isWaitingForResponse false for the whole fetch window — the loading
  // indicator only appeared once fetchMessages() resolved, instead of
  // immediately on the conversation switching in.
  const [isInitialFetching, setIsInitialFetching] = useState(false);
  // Bumped to force the subscribe effect to re-run after the Observable errors
  // (it is terminal on error, so live updates would otherwise stay dead for the
  // rest of the session). retryCountRef drives exponential backoff and is reset
  // on any successful event; retryTimerRef lets cleanup cancel a pending retry.
  const [subscriptionRetryKey, setSubscriptionRetryKey] = useState(0);
  const subscriptionRetryCountRef = useRef(0);
  const subscriptionRetryTimerRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  // Monotonic counter guarding fetchMessages against out-of-order resolution
  // (see fetchMessages below).
  const fetchSeqRef = useRef(0);

  // Per-tool-call temporal phase for the current progress message: 'before' if
  // the tool call first appeared while the answer text was still empty (render
  // above the text), 'after' if answer text had already started streaming
  // (render below). Decided once, at first sighting of each tool-call id, and
  // cleared whenever the progress message ends (persisted answer / switch /
  // reset) so the next turn starts fresh.
  const toolCallPhaseRef = useRef<Map<string, "before" | "after">>(new Map());

  // Diagnostic: log whenever the messages array changes, alongside the
  // current conversationId, so we can see if stale messages linger after
  // conversationId clears to null (which would point to the leak being
  // in the rendering layer rather than the fetch/clear path).
  useEffect(() => {
    console.log("[useAssistantMessages messages snapshot]", {
      conversationId:
        conversationId == null ? "<null>" : conversationId.substring(0, 20) + "...",
      messageCount: messages.length,
      firstMessagePreview:
        messages[0]?.text?.substring(0, 40) ?? "(no messages)",
      lastMessageId: messages[messages.length - 1]?.id?.substring(0, 20) ?? "(none)",
    });
  }, [messages, conversationId]);
  const onAIMessageRef = useRef(onAIMessage);
  useEffect(() => {
    onAIMessageRef.current = onAIMessage;
  });

  const client = useApolloClient();

  // ── Derived state ──────────────────────────────────────────────────

  const displayMessages = messages.filter((m) => !isProgressMessage(m));
  const progressMessage = messages.find((m) => isProgressMessage(m));
  const lastMessage = displayMessages[displayMessages.length - 1];

  const isWaitingForResponse =
    isSending ||
    waitingForAIResponse ||
    !!lastMessage?.isFromUser ||
    !!progressMessage ||
    isInitialFetching;

  const responseTimedOut = responseTimeoutTriggered === lastMessage?.id;

  const hasOpenPendingQuestion = deriveHasOpenPendingQuestion(
    displayMessages,
    lastMessage,
  );

  // ── Clear waitingForAIResponse when AI message arrives ─────────────

  useEffect(() => {
    if (
      waitingForAIResponse &&
      lastMessage &&
      !lastMessage.isFromUser &&
      lastMessage.createdAt
    ) {
      setWaitingForAIResponse(false);
    }
  }, [waitingForAIResponse, lastMessage]);

  // ── 5-minute timeout ───────────────────────────────────────────────

  useEffect(() => {
    if (!responseTimeoutTriggered && isWaitingForResponse && lastMessage?.id) {
      const timeoutId = setTimeout(
        () => {
          setResponseTimeoutTriggered(lastMessage.id);
          toast.error("Zeitüberschreitung", {
            description:
              "Die Antwort dauert zu lange. Bitte versuchen Sie es erneut.",
          });
        },
        5 * 60 * 1000,
      );
      return () => clearTimeout(timeoutId);
    }
  }, [isWaitingForResponse, lastMessage?.id, responseTimeoutTriggered]);

  // ── Clear state when conversation changes ─────────────────────────

  const prevConversationId = useRef(conversationId);
  // Tracks the *current* conversationId so in-flight fetches can detect that
  // the user navigated away and discard their result instead of clobbering
  // the new conversation's messages.
  const activeConversationIdRef = useRef(conversationId);
  useEffect(() => {
    activeConversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    const prevId = prevConversationId.current;
    prevConversationId.current = conversationId;

    // Reset chat state ONLY when actually switching between two different
    // existing conversations (e.g. operator navigates to a different ticket).
    //
    // Other transitions are deliberately no-ops:
    //   - null → real-id: first message of a new chat — keep optimistic
    //     messages so the user's just-sent message stays visible until the
    //     server-confirmed copy arrives.
    //   - real-id → null: transient resolver/auth blip. The server-side
    //     `inline_assistant_conversation_id` resolver short-circuits to
    //     null whenever `info.context.user` is missing (e.g. an in-flight
    //     Apollo request fires during a Keycloak token refresh). The next
    //     poll typically returns the real ID again; we must not wipe local
    //     chat state on what is almost certainly a momentary mismatch.
    //   - same → same: defensive no-op.
    if (prevId == null && conversationId != null) return;
    if (prevId != null && conversationId == null) return;
    if (prevId === conversationId) return;

    const trunc = (v: string | null | undefined) =>
      v == null ? "<null>" : v.substring(0, 20) + "...";
    console.log("[useAssistantMessages conversationId switch — resetting]", {
      from: trunc(prevId),
      to: trunc(conversationId),
    });

    setMessages([]);
    setIsSending(false);
    setWaitingForAIResponse(false);
    setResponseTimeoutTriggered(null);
    setSubscriptionError(false);
    subscriptionRetryCountRef.current = 0;
    toolCallPhaseRef.current.clear();
  }, [conversationId]);

  // ── Fetch + subscribe ──────────────────────────────────────────────

  // Apollo InvariantError #89 ("Store reset while query was in flight") fires
  // when a staff tenant switch triggers resetStore(). Treat as transient and retry; without
  // this, the toast pops and the previous ticket's chat stays in state because
  // setMessages is never called.
  const isApolloStoreReset = (errMsg: string | undefined) =>
    !!errMsg && (
      errMsg.toLowerCase().includes("store reset while query was in flight") ||
      (errMsg.includes("go.apollo.dev") && errMsg.includes("89"))
    );

  const fetchMessages = useCallback(async (clearProgress = false) => {
    // Snapshot at fetch-start. If the user switches tickets before this
    // resolves, activeConversationIdRef.current will have moved on and we
    // must discard the result rather than overwrite the new conversation's
    // messages with the old conversation's data.
    const fetchConversationId = conversationId;
    // Two persisted-message subscription events (e.g. two sensitive-tool
    // confirmations back to back) each fire their own fetchMessages(true).
    // Without a monotonic guard, an EARLIER call that resolves LATER (network
    // jitter) can overwrite state with stale data and silently erase the
    // newer message's choiceOptions/buttons. Only the most recently STARTED
    // fetch is allowed to apply its result.
    const fetchSeq = ++fetchSeqRef.current;
    const MAX_STORE_RESET_ATTEMPTS = 3;
    let storeResetAttempts = 0;
    while (true) {
      try {
        const result = await client.query<{
          assistantConversation: {
            messages: { edges: { node: AssistantMessage }[] };
          };
        }>({
          query: ASSISTANT_CONVERSATION_QUERY,
          variables: { conversationId: fetchConversationId },
          fetchPolicy: "network-only",
        });
        // Stale-fetch guards: bail if the active conversation has changed,
        // or if a fetch started AFTER this one has already applied its
        // (necessarily fresher-or-equal) result.
        if (activeConversationIdRef.current !== fetchConversationId) {
          console.log(
            "[useAssistantMessages] Discarding stale fetch result",
            { fetched: fetchConversationId, active: activeConversationIdRef.current },
          );
          return;
        }
        if (fetchSeq !== fetchSeqRef.current) {
          console.log(
            "[useAssistantMessages] Discarding out-of-order fetch result",
            { fetchSeq, latest: fetchSeqRef.current },
          );
          return;
        }
        const edges = result.data?.assistantConversation?.messages?.edges ?? [];
        const fetchedMessages = edges.map((e) => e.node);
        // If the latest persisted message is an assistant answer (no agent.id,
        // has createdAt), the turn completed — drop any lingering progress
        // message even when the caller didn't ask to. This is what lets the
        // polling fallback recover from a dropped subscription: without it, a
        // broken WebSocket means the persisted-AI subscription event (the only
        // path that normally calls fetchMessages(true)) never arrives, so the
        // progress message — and thus isWaitingForResponse — stays stuck on.
        const lastFetched = fetchedMessages[fetchedMessages.length - 1];
        const answerLanded =
          !!lastFetched && !lastFetched.isFromUser && !!lastFetched.createdAt;
        const dropProgress = clearProgress || answerLanded;
        if (dropProgress) toolCallPhaseRef.current.clear();
        setMessages((prev) => {
          const localMessages = prev.filter((m) => {
            if (isProgressMessage(m)) return !dropProgress;
            if (m.id.startsWith("temp-")) {
              const alreadyFetched = fetchedMessages.some(
                (fm) => fm.text === m.text && fm.isFromUser,
              );
              return !alreadyFetched;
            }
            return false;
          });
          return [...fetchedMessages, ...localMessages];
        });
        if (answerLanded) setWaitingForAIResponse(false);
        setIsInitialFetching(false);
        return;
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        if (isApolloStoreReset(msg) && storeResetAttempts < MAX_STORE_RESET_ATTEMPTS - 1) {
          // Conversation may have changed during the failed attempt — bail
          // before sleeping so we don't keep retrying for an old conversation.
          if (activeConversationIdRef.current !== fetchConversationId) return;
          storeResetAttempts += 1;
          console.log(
            `[useAssistantMessages] Apollo store-reset (attempt ${storeResetAttempts}/${MAX_STORE_RESET_ATTEMPTS}) — retrying after ${150 * storeResetAttempts}ms`,
          );
          await new Promise((r) => setTimeout(r, 150 * storeResetAttempts));
          continue;
        }
        // Final failure (or non-reset error) — guard apply with the same checks.
        if (activeConversationIdRef.current !== fetchConversationId) return;
        if (fetchSeq !== fetchSeqRef.current) return;
        console.error("Failed to fetch assistant messages:", error);
        // Clear messages so the previous conversation's chat doesn't linger.
        setMessages([]);
        if (!isApolloStoreReset(msg)) {
          toast.error("Nachrichten konnten nicht geladen werden", {
            description: "Bitte laden Sie die Seite neu.",
          });
        }
        setWaitingForAIResponse(false);
        setIsInitialFetching(false);
        return;
      }
    }
  }, [conversationId, client]);

  useEffect(() => {
    if (!conversationId) return;

    setIsInitialFetching(true);
    void fetchMessages();
    setSubscriptionError(false);

    const subscription = client
      .subscribe<{ assistantConversation: AssistantMessage }>({
        query: ASSISTANT_CONVERSATION_SUBSCRIPTION,
        variables: { conversationId },
      })
      .subscribe({
        next: ({ data }) => {
          setSubscriptionError(false);
          subscriptionRetryCountRef.current = 0;
          if (!data?.assistantConversation) return;
          const newMsg = data.assistantConversation;

          // Progress message (no createdAt) — replace previous progress msg.
          //
          // Keep showing it (including batches where every tool call is in a
          // terminal COMPLETED/FAILED state) until the persisted assistant
          // message arrives and atomically replaces it via fetchMessages(true)
          // in the branch below.
          //
          // We deliberately do NOT discard "all terminal" progress events here.
          // A multi-step turn emits progress repeatedly (search RUNNING →
          // search COMPLETED → next search RUNNING → …). Dropping the progress
          // message on each completed batch left `progressMessage` undefined
          // between steps, so the generic <WaitingIndicator/> ("thinking…")
          // rendered, then the tool-call list reappeared on the next step —
          // making the UI flicker back and forth. Holding the completed
          // tool-call list in place until the answer lands is both flicker-free
          // and better UX (the user sees "Searched ✓" rather than a spinner).
          //
          // Progress arrives as full snapshots, but the streamed-text turn and
          // the deep-search tool phase emit on the same subscription in
          // parallel. A tool-only snapshot (e.g. "deep_search" RUNNING) carries
          // empty `text`, and a text-only streaming snapshot carries no
          // `toolCalls`. Replacing the whole progress message on each event
          // therefore let each phase clobber the other — the visible streamed
          // answer was wiped the instant the deep-search indicator arrived.
          // Merge instead: keep the last non-empty text and union tool calls by
          // id, so text and the tool-call list coexist rather than overwrite.
          //
          // Tool calls are then split into temporal order around the text: a
          // tool call seen for the first time while the answer was still empty
          // ran *before* the answer (renders above it); one that first appears
          // after text has started streaming (e.g. a deep search triggered once
          // an initial reply was already on screen) renders *below* the text.
          if (!newMsg.createdAt) {
            setMessages((prev) => {
              const prevProgress = prev.find((m) => isProgressMessage(m));
              const withoutProgress = prev.filter((m) => m.createdAt);

              const mergedText =
                newMsg.text && newMsg.text.length > 0
                  ? newMsg.text
                  : prevProgress?.text ?? newMsg.text;

              const byId = new Map<string, AssistantToolCall>();
              for (const tc of prevProgress?.toolCalls ?? []) byId.set(tc.id, tc);
              for (const tc of newMsg.toolCalls ?? []) byId.set(tc.id, tc);

              // Whether answer text already existed *before* this snapshot —
              // decides the phase of any tool-call id we're seeing for the
              // first time in this event.
              const textExistedBefore = !!(
                prevProgress?.text && prevProgress.text.length > 0
              );
              const phases = toolCallPhaseRef.current;
              for (const id of byId.keys()) {
                if (!phases.has(id)) {
                  phases.set(id, textExistedBefore ? "after" : "before");
                }
              }

              const before: AssistantToolCall[] = [];
              const after: AssistantToolCall[] = [];
              for (const tc of byId.values()) {
                (phases.get(tc.id) === "after" ? after : before).push(tc);
              }

              const merged: AssistantMessage = {
                ...prevProgress,
                ...newMsg,
                text: mergedText,
                toolCalls: before.length > 0 ? before : undefined,
                toolCallsAfterText: after.length > 0 ? after : undefined,
              };
              return [...withoutProgress, merged];
            });
            return;
          }

          // Persisted AI message — refetch canonical state from server
          if (!newMsg.isFromUser) {
            // Do NOT clear waitingForAIResponse here. Let fetchMessages
            // resolve first; the clearWaitingForAIResponse effect will
            // handle it once the AI message appears in the messages array.
            // Clearing it immediately causes a window where messages=[]
            // and all waiting flags are false simultaneously, making the
            // status badge flicker away before the response is visible.
            // Pass clearProgress=true so the progress message is replaced
            // atomically with the persisted message — no blank-frame flash.
            toolCallPhaseRef.current.clear();
            void fetchMessages(true);
            onAIMessageRef.current?.();
            return;
          }

          // Persisted user message — replace optimistic temp message
          setMessages((prev) => {
            const cleaned = prev.filter(
              (m) => !m.id.startsWith("temp-") && m.id !== newMsg.id,
            );
            return [...cleaned, newMsg];
          });
        },
        error: (error) => {
          console.error("Subscription error:", error);
          setSubscriptionError(true);
          // The subscription Observable is terminal on error — no further `next`
          // events arrive even if the socket reconnects, so the persisted-AI
          // event that would normally clear the progress message is lost. Fetch
          // once immediately (the 10s polling fallback also keeps running) so a
          // turn that completed around the disconnect resolves without waiting.
          void fetchMessages();
          // Re-establish live updates by re-running this effect after an
          // exponential backoff (1s, 2s, 4s … capped at 30s). Keeps retrying so
          // a transient drop (wifi blip, token refresh) restores streaming
          // without a page reload; retryCountRef resets on the next success.
          const attempt = subscriptionRetryCountRef.current;
          subscriptionRetryCountRef.current = attempt + 1;
          const delay = Math.min(30_000, 1000 * 2 ** attempt);
          if (subscriptionRetryTimerRef.current) {
            clearTimeout(subscriptionRetryTimerRef.current);
          }
          subscriptionRetryTimerRef.current = setTimeout(() => {
            setSubscriptionRetryKey((k) => k + 1);
          }, delay);
        },
      });

    return () => {
      subscription.unsubscribe();
      if (subscriptionRetryTimerRef.current) {
        clearTimeout(subscriptionRetryTimerRef.current);
        subscriptionRetryTimerRef.current = null;
      }
    };
  }, [conversationId, client, fetchMessages, subscriptionRetryKey]);

  // ── Polling fallback ───────────────────────────────────────────────

  useEffect(() => {
    // Poll whenever the UI is in any waiting state (not just the
    // waitingForAIResponse/isSending subset) so a dropped subscription is always
    // backstopped — e.g. a freshly mounted conversation awaiting its first reply
    // has only lastMessage?.isFromUser set, yet still needs the fallback.
    //
    // Stop once the turn has timed out: isWaitingForResponse stays true for any
    // conversation ending on a user message (e.g. a past turn whose AI reply
    // never persisted), so without the responseTimedOut guard such a conversation
    // would poll every 10s forever.
    if (!conversationId || !isWaitingForResponse || responseTimedOut) return;

    const pollInterval = setInterval(() => {
      void fetchMessages();
    }, 10_000);

    return () => clearInterval(pollInterval);
  }, [conversationId, isWaitingForResponse, responseTimedOut, fetchMessages]);

  // ── Send follow-up mutation ────────────────────────────────────────

  const [sendMutation] = useMutation(ASSISTANT_SEND_MESSAGE_MUTATION, {
    onCompleted: () => {
      setResponseTimeoutTriggered(null);
      setIsSending(false);
      setWaitingForAIResponse(true);
    },
    onError: (error) => {
      console.error("Failed to send assistant message:", error);
      toast.error("Nachricht konnte nicht gesendet werden", {
        description: "Bitte versuchen Sie es erneut.",
      });
      setIsSending(false);
      setWaitingForAIResponse(false);
    },
  });

  // ── Public API ─────────────────────────────────────────────────────

  const addOptimisticUserMessage = useCallback((text: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: `temp-${Date.now()}`,
        createdAt: new Date().toISOString(),
        text,
        isFromUser: true,
        agent: { id: "current-user" },
      },
    ]);
  }, []);

  const sendMessage = useCallback(
    (text: string, attachmentIds?: string[]) => {
      if (!conversationId) return;
      addOptimisticUserMessage(text);
      setIsSending(true);
      void sendMutation({
        variables: { conversationId, text, panelContext, attachmentIds },
      });
    },
    [conversationId, panelContext, sendMutation, addOptimisticUserMessage],
  );

  const updateMessageFeedback = useCallback(
    (messageId: string, feedback: ChatRating, feedbackComment: string) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, feedback, feedbackComment } : m)),
      );
    },
    [],
  );

  const reset = useCallback(() => {
    setMessages([]);
    setIsSending(false);
    setWaitingForAIResponse(false);
    setResponseTimeoutTriggered(null);
    setSubscriptionError(false);
    toolCallPhaseRef.current.clear();
  }, []);

  return {
    messages,
    displayMessages,
    progressMessage,
    isWaitingForResponse,
    isInitialFetching,
    isSending,
    responseTimedOut,
    subscriptionError,
    hasOpenPendingQuestion,
    sendMessage,
    addOptimisticUserMessage,
    setSending: setIsSending,
    reset,
    updateMessageFeedback,
  };
}
